import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, Box, Button, Chip, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';

import { METHODS, CallMethod, CallDirection } from '../calls/logCall';
import { CRM_CONTACT_QUICK_SEARCH } from '../contacts/contacts.queries';
import { SUGGEST_DELAY_MS } from '../contacts/ContactSearchBox';
import { CRM_CALL_LOG_PERSON } from './callLog.queries';
import {
  CallerCount,
  callerCountName,
  callerKey,
  CallLogState,
  DIRECTION_CHOICES,
  FOLLOW_UP_CHOICES,
  FollowUpId,
  hasLogFilters,
  NOTES_SEARCH,
  RECORDING_CHOICES,
  RecordingId,
  SOURCE_CHOICES,
  SourceId,
  WHEN_CHOICES,
  WhenId,
} from './callLog';

interface Suggestion {
  id: number;
  fullName: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  city: string | null;
  countryCode: string | null;
}

const suggestionName = (s: Suggestion) => s.fullName ?? s.primaryEmail ?? `Contact #${s.id}`;

/** Picks whose calls to show: typing suggests people (crmContactSearch), picking one filters to them. */
const PersonPicker = ({ person, onPick }: { person: number | null; onPick: (contactId: number | null) => void }) => {
  const [input, setInput] = useState('');
  const [asked, setAsked] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setAsked(input.trim()), SUGGEST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const { data, loading } = useQuery<{ crmContactSearch: { items: Array<Suggestion> } }>(CRM_CONTACT_QUICK_SEARCH, {
    variables: { text: asked },
    skip: asked.length < 2 || person != null,
    // Never cached: its matchedOn would overwrite the cached rows the contact list shows.
    fetchPolicy: 'no-cache',
  });
  const chosen = useQuery<{ crmContact: { id: number; fullName: string | null; primaryEmail: string | null } }>(CRM_CALL_LOG_PERSON, {
    variables: { id: person },
    skip: person == null,
  });

  if (person != null) {
    const contact = chosen.data?.crmContact;
    const label = contact ? contact.fullName ?? contact.primaryEmail ?? `Contact #${person}` : chosen.error ? `Contact #${person}` : 'Loading…';
    return (
      <Chip
        icon={<PersonOutlineIcon />}
        label={`Calls to ${label}`}
        onDelete={() => onPick(null)}
        color="primary"
        variant="outlined"
        sx={{ height: 40, borderRadius: 2.5, fontWeight: 500, justifySelf: 'start' }}
      />
    );
  }

  const options = asked.length >= 2 && input.trim() === asked ? data?.crmContactSearch.items ?? [] : [];

  return (
    <Autocomplete<Suggestion>
      options={options}
      filterOptions={(all) => all}
      getOptionLabel={suggestionName}
      value={null}
      inputValue={input}
      onInputChange={(_event, text, reason) => reason !== 'reset' && setInput(text)}
      onChange={(_event, picked) => {
        if (!picked) return;
        setInput('');
        onPick(picked.id);
      }}
      loading={asked.length >= 2 && loading}
      noOptionsText={asked.length < 2 ? 'Type a name, email or phone' : 'Nobody matches'}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {suggestionName(option)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[option.primaryPhone, option.primaryEmail, [option.city, option.countryCode].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="One person's calls"
          inputProps={{ ...params.inputProps, 'aria-label': 'Person called' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

/** The notes search: sent after a pause, once it is long enough for the API. */
const NotesSearch = ({ value, onSearch }: { value: string; onSearch: (text: string) => void }) => {
  const [input, setInput] = useState(value);

  // Changed from outside (cleared, back button): show it.
  useEffect(() => setInput(value), [value]);

  useEffect(() => {
    const text = input.trim();
    if (text === value.trim()) return undefined;
    if (text.length > 0 && text.length < NOTES_SEARCH.min) return undefined;
    const timer = setTimeout(() => onSearch(text), 400);
    return () => clearTimeout(timer);
  }, [input, value, onSearch]);

  const short = input.trim().length > 0 && input.trim().length < NOTES_SEARCH.min;

  return (
    <TextField
      size="small"
      placeholder="Search the notes"
      value={input}
      onChange={(event) => setInput(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !short) onSearch(input.trim());
        if (event.key === 'Escape' && input) {
          setInput('');
          onSearch('');
        }
      }}
      helperText={short ? `Type at least ${NOTES_SEARCH.min} characters` : undefined}
      inputProps={{ maxLength: NOTES_SEARCH.max, 'aria-label': 'Search the notes' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: 'text.secondary' }} />
          </InputAdornment>
        ),
      }}
    />
  );
};

const n = (value: number) => value.toLocaleString('en-US');

interface CallLogFiltersProps {
  state: CallLogState;
  callers: Array<CallerCount> | undefined;
  onChange: (change: Partial<CallLogState>) => void;
  onClear: () => void;
}

/** A menu whose empty choice ("Any time") is shown, not left blank under its label. */
const SHOW_EMPTY = { InputLabelProps: { shrink: true }, SelectProps: { displayEmpty: true } } as const;

/** Two to a row on a phone, side by side from a tablet up. */
const SELECT_SX = { minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 calc(50% - 10px)', sm: '0 0 auto' } } as const;

/**
 * Who, what and when. Every choice is in the URL. The caller menu counts each
 * caller's calls under the other filters, so it lists only callers with calls.
 */
export const CallLogFilters = ({ state, callers, onChange, onClear }: CallLogFiltersProps) => {
  // A caller chosen by link who has no calls under these filters, or whose counts are still on
  // their way, is still in the menu.
  const chosenMissing = state.caller && state.caller !== 'me' && !callers?.some((caller) => callerKey(caller) === state.caller);

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <NotesSearch value={state.notes} onSearch={(notes) => onChange({ notes })} />
        <PersonPicker person={state.person} onPick={(person) => onChange({ person })} />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Caller"
          value={state.caller}
          onChange={(event) => onChange({ caller: event.target.value })}
          sx={SELECT_SX}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
            renderValue: (value) => {
              if (!value) return 'Anyone';
              if (value === 'me') return 'You';
              const caller = callers?.find((c) => callerKey(c) === value);
              return caller ? callerCountName(caller) : String(value).startsWith('a') ? `Admin #${String(value).slice(1)}` : `Contact #${String(value).slice(1)}`;
            },
          }}
        >
          <MenuItem value="">Anyone</MenuItem>
          <MenuItem value="me">You</MenuItem>
          {(callers ?? [])
            .filter((caller) => !caller.isYou)
            .map((caller) => (
              <MenuItem key={callerKey(caller)} value={callerKey(caller)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                  <span>{callerCountName(caller)}</span>
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {n(caller.count)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          {chosenMissing && (
            <MenuItem value={state.caller}>{state.caller.startsWith('a') ? `Admin #${state.caller.slice(1)}` : `Contact #${state.caller.slice(1)}`}</MenuItem>
          )}
        </TextField>

        <TextField
          select
          size="small"
          label="When"
          {...SHOW_EMPTY}
          value={state.when ?? ''}
          onChange={(event) => onChange({ when: (event.target.value || null) as WhenId | null })}
          sx={SELECT_SX}
        >
          <MenuItem value="">Any time</MenuItem>
          {WHEN_CHOICES.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>

        {state.when === 'between' && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={state.from}
              onChange={(event) => onChange({ from: event.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={SELECT_SX}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={state.to}
              onChange={(event) => onChange({ to: event.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={SELECT_SX}
            />
          </>
        )}

        <TextField
          select
          size="small"
          label="How"
          {...SHOW_EMPTY}
          value={state.method ?? ''}
          onChange={(event) => onChange({ method: (event.target.value || null) as CallMethod | null })}
          sx={SELECT_SX}
        >
          <MenuItem value="">Any way</MenuItem>
          {METHODS.map((method) => (
            <MenuItem key={method.value} value={method.value}>
              {method.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Direction"
          {...SHOW_EMPTY}
          value={state.direction ?? ''}
          onChange={(event) => onChange({ direction: (event.target.value || null) as CallDirection | null })}
          sx={SELECT_SX}
        >
          <MenuItem value="">Either</MenuItem>
          {DIRECTION_CHOICES.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Follow-up"
          value={state.followUp ?? ''}
          onChange={(event) => onChange({ followUp: (event.target.value || null) as FollowUpId | null })}
          sx={SELECT_SX}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => FOLLOW_UP_CHOICES.find((choice) => choice.id === value)?.label ?? 'Any',
          }}
        >
          <MenuItem value="">Any</MenuItem>
          {FOLLOW_UP_CHOICES.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              <Box>
                {choice.label}
                <Typography variant="caption" color="text.secondary" component="div">
                  {choice.hint}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Recorded"
          {...SHOW_EMPTY}
          value={state.source ?? ''}
          onChange={(event) => onChange({ source: (event.target.value || null) as SourceId | null })}
          sx={SELECT_SX}
        >
          <MenuItem value="">Anywhere</MenuItem>
          {SOURCE_CHOICES.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Recording"
          {...SHOW_EMPTY}
          value={state.recording ?? ''}
          onChange={(event) => onChange({ recording: (event.target.value || null) as RecordingId | null })}
          sx={SELECT_SX}
        >
          <MenuItem value="">Any</MenuItem>
          {RECORDING_CHOICES.map((choice) => (
            <MenuItem key={choice.id} value={choice.id}>
              {choice.label}
            </MenuItem>
          ))}
        </TextField>

        {hasLogFilters(state) && (
          <Button size="small" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </Box>
    </Box>
  );
};
